import { Flex, Text, forwardRef } from '@chakra-ui/react';
import Decimal from 'decimal.js';
import { useMemo } from 'react';

type Value = Decimal | string | number | undefined;

export interface CurrencyLabelProps {
  value: Value | Value[];
}

export const CurrencyLabel = forwardRef((props: CurrencyLabelProps, ref) => {
  const { value, ...otherProps } = props;

  const sum = useMemo(() => {
    if (!value || !Array.isArray(value)) return 0;

    return value
      .reduce<Decimal>((sum, n) => sum.add(new Decimal(n || 0)), new Decimal(0))
      .toNumber();
  }, [value]);

  const rationaliedValue = useMemo(() => {
    if (Array.isArray(value)) {
      return sum;
    } else {
      return new Decimal(value || 0).toNumber();
    }
  }, [sum, value]);

  const textProps = {
    color: rationaliedValue >= 0 ? undefined : 'var(--chakra-colors-red-200)',
  };

  const displayValue = useMemo(() => {
    const sDecimal = rationaliedValue.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    if (rationaliedValue < 0) {
      return `(${sDecimal})`;
    } else {
      return sDecimal;
    }
  }, [rationaliedValue]);

  return (
    <Flex
      dir="horizontal"
      alignContent={'center'}
      ref={ref}
      {...otherProps}
      {...textProps}
    >
      <Text as="kbd">$</Text>
      <Text as="kbd" flex={1} textAlign={'right'} noOfLines={1}>
        {displayValue}
      </Text>
    </Flex>
  );
});
